import { useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { initialGesture, rowVisual, step, type GestureEffect, type GestureEvent, type GestureState } from '../lib/gesture'
import { MOTION, SWIPE } from '../lib/constants'
import { haptic } from '../lib/haptics'
import { useLatest } from './useLatest'

// The swipe rows of one list (ux/04), wired from pointer events to the pure
// reducer in lib/gesture.ts. One instance per list, so only one row is ever
// open. The reducer decides; this hook only feeds it events, plays its
// effects (haptics, the slide-out, then the callback) and exposes what each
// row should look like.

export type Leaving = 'right' | 'left'

export interface SwipeOptions {
  /** Phone (shop) only: desktop ignores rightward drags (ux/04). */
  swipeRight: boolean
  onBasket: (id: string) => void
  onDelete: (id: string) => void
  onEdit: (id: string) => void
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export function useSwipeRows({ swipeRight, onBasket, onDelete, onEdit }: SwipeOptions) {
  const [gesture, setGesture] = useState<GestureState>(initialGesture)
  const [leaving, setLeaving] = useState<Record<string, { side: Leaving; dx: number }>>({})
  const gestureRef = useRef(gesture)
  const leavingRef = useLatest(leaving)
  // Where the current press started and whether its row was open, so a
  // planning list can refuse rightward travel past the closed position.
  const press = useRef<{ x0: number; wasOpen: boolean } | null>(null)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const callbacks = useLatest({ onBasket, onDelete, onEdit })

  useEffect(() => () => timers.current.forEach(clearTimeout), [])

  // `dx` is where the finger let go: the row leaves from there.
  const leave = useCallback((id: string, side: Leaving, then: () => void, dx = 0) => {
    setLeaving(l => ({ ...l, [id]: { side, dx } }))
    const ms = prefersReducedMotion()
      ? MOTION.reducedMotionFadeMs
      : side === 'right' ? MOTION.slideOut.ms : MOTION.deleteSlideOut.ms
    timers.current.push(setTimeout(() => {
      then()
      setLeaving(l => { const { [id]: _gone, ...rest } = l; return rest })
    }, ms))
  }, [])

  const play = useCallback((fx: GestureEffect, releasedAt: number) => {
    switch (fx.type) {
      case 'haptic': haptic(fx.pattern); break
      case 'toBasket': leave(fx.id, 'right', () => callbacks.current.onBasket(fx.id), releasedAt); break
      case 'delete': leave(fx.id, 'left', () => callbacks.current.onDelete(fx.id), releasedAt); break
      case 'edit': callbacks.current.onEdit(fx.id); break
      case 'settle': break // the row's transform follows the state; CSS animates it
    }
  }, [leave, callbacks])

  const dispatch = useCallback((e: GestureEvent) => {
    const releasedAt = gestureRef.current.drag?.dx ?? 0
    const { state, effects } = step(gestureRef.current, e)
    gestureRef.current = state
    setGesture(state)
    effects.forEach(fx => play(fx, releasedAt))
  }, [play])

  /** Close the open row: scrolling, opening a sheet, tapping outside. */
  const close = useCallback(() => {
    if (gestureRef.current.open) dispatch({ type: 'close' })
  }, [dispatch])

  // A press anywhere outside a swipe row closes the open one. The revealed
  // Delete button sits in its own track beside the row, not inside it, so it
  // counts as inside too — else its press closed the row and the button
  // collapsed before the click landed (only a full swipe deleted).
  useEffect(() => {
    if (!gesture.open) return
    const onDown = (e: PointerEvent) => {
      if (!(e.target as Element | null)?.closest?.('[data-swipe-row], [data-swipe-ignore]')) close()
    }
    document.addEventListener('pointerdown', onDown, true)
    return () => document.removeEventListener('pointerdown', onDown, true)
  }, [gesture.open, close])

  const bind = useCallback((id: string) => ({
    'data-swipe-row': id,
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0 || Object.keys(leavingRef.current).length) return
      // The check button and the revealed Delete button are their own controls.
      if ((e.target as Element).closest('[data-swipe-ignore]')) return
      const el = e.currentTarget
      press.current = { x0: e.clientX, wasOpen: gestureRef.current.open === id }
      try { el.setPointerCapture(e.pointerId) } catch { /* synthetic events in tests */ }
      dispatch({ type: 'down', id, x: e.clientX, t: e.timeStamp, width: el.getBoundingClientRect().width || 360 })
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      if (!gestureRef.current.drag || !press.current) return
      let x = e.clientX
      if (!swipeRight) x = Math.min(x, press.current.x0 + (press.current.wasOpen ? SWIPE.revealPx : 0))
      dispatch({ type: 'move', x, t: e.timeStamp })
    },
    onPointerUp: () => {
      if (!gestureRef.current.drag) return
      press.current = null
      dispatch({ type: 'up' })
    },
    // The browser took the pointer for a vertical scroll: snap back, no action.
    onPointerCancel: () => {
      if (!gestureRef.current.drag) return
      press.current = null
      dispatch({ type: 'cancel' })
    },
  }), [dispatch, swipeRight, leavingRef])

  const visual = useCallback((id: string) => {
    const v = rowVisual(gesture, id)
    const l = leaving[id]
    return l
      ? { ...v, dx: l.dx, deleteWidth: l.dx < 0 ? -l.dx : 0, leaving: l.side }
      : { ...v, leaving: null as Leaving | null }
  }, [gesture, leaving])

  /** Run the basket slide-out for a non-gesture path (the check button). */
  const sendToBasket = useCallback((id: string) => {
    if (leavingRef.current[id]) return
    leave(id, 'right', () => callbacks.current.onBasket(id))
  }, [leave, leavingRef, callbacks])

  return { bind, visual, close, sendToBasket, openId: gesture.open }
}

export type RowVisual = ReturnType<ReturnType<typeof useSwipeRows>['visual']>
